import { isPossibleSlug } from "../lib/slug";
import { announceSignOut } from "../../../../shared/hooks/useSignOutNotice";

/**
 * WHERE A CLIENT LANDS AFTER THEIR SESSION ENDED (IdentityProvider's
 * `onSessionEnded`, wired in main.tsx).
 *
 * On a professional's pages (`/<slug>`, `/<slug>/book/<step>`,
 * `/<slug>/book/reset`): that professional's sign-in step, keeping the slug.
 * Nothing else is carried: no query (the selection is re-chosen after sign-in,
 * as the sign-in step already does) and never a token.
 *
 * Anywhere else (`/`, `/a/<token>`): null, stay. Those pages have no sign-in
 * of their own, the appointment link is a bearer capability that needs no
 * session, and an /a/ URL is never copied into another one.
 */
export function signInPathAfterSessionEnd(pathname: string): string | null {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  const [slug, book, step, ...rest] = segments;
  if (!isPossibleSlug(slug) || rest.length > 0) return null;
  if (book !== undefined && (book !== "book" || step === undefined)) return null;
  return `/${slug}/book/signin`;
}

/** The router surface this needs; the real one is the data router's. */
export interface SessionEndRouter {
  readonly state: { readonly location: { readonly pathname: string } };
  navigate(to: string): unknown;
}

/**
 * Route to sign-in after a session end, unless already there or there is none.
 *
 * Records the UNEXPECTED sign-out for the sign-in step's line ("Your session
 * ended. Sign in to continue.", shared/hooks/useSignOutNotice.ts) only when
 * there IS a sign-in step to land on. Not on `/` or `/a/<token>`: neither has
 * a sign-in, the appointment link works without a session, and a line telling
 * the person to sign in where they cannot would be untrue; recorded there, it
 * would also surface later on an unrelated sign-in.
 */
export function routeAfterSessionEnd(router: SessionEndRouter): void {
  const here = router.state.location.pathname;
  const target = signInPathAfterSessionEnd(here);
  if (target === null) return;
  announceSignOut("session-ended");
  if (target === here) return;
  void router.navigate(target);
}
